import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { ArrowRight, Printer, History } from "lucide-react";

const fmtDateTime = (iso) => new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });

const esc = (v) => String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

function printHistory(data) {
  const rows = data.history.map((h) =>
    `<tr><td>${esc(fmtDateTime(h.changed_at))}</td><td>${esc(h.subject?.name)}</td><td>${esc(h.from_level || "Non inscrit")} → ${esc(h.to_level || "Non inscrit")}</td><td>${esc(h.changed_by_name)}</td></tr>`
  ).join("");
  const html = `<html><head><title>Progression — ${esc(data.student.last_name)} ${esc(data.student.first_name)}</title>
    <style>body{font-family:Georgia,serif;padding:40px;color:#222}h1{font-size:22px}h2{font-size:14px;color:#7a1f2b;text-transform:uppercase;letter-spacing:1px}
    table{border-collapse:collapse;width:100%;margin-top:16px}td,th{border:1px solid #ccc;padding:8px 10px;font-size:13px;text-align:left}th{background:#f5efe6}</style></head>
    <body><h2>Lafayette Progress — Progression des niveaux de langue</h2>
    <h1>${esc(data.student.last_name.toUpperCase())} ${esc(data.student.first_name)}${data.student.class_name ? " · " + esc(data.student.class_name) : ""}</h1>
    <table><thead><tr><th>Date</th><th>Langue</th><th>Changement</th><th>Modifié par</th></tr></thead><tbody>${rows}</tbody></table>
    </body></html>`;
  const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
  const w = window.open(url, "_blank");
  if (w) {
    w.onload = () => {
      w.print();
      URL.revokeObjectURL(url);
    };
  }
}

export function LevelHistoryContent({ studentId }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/students/${studentId}/level-history`).then(({ data }) => setData(data)).catch(() => setData({ history: [] }));
  }, [studentId]);

  if (!data) return <div className="text-sm text-[var(--ink-soft)]">Chargement...</div>;
  if (!data.history.length) {
    return <div className="text-sm text-[var(--muted)] italic" data-testid="level-history-empty">Aucun changement de niveau enregistré pour le moment.</div>;
  }
  return (
    <div data-testid="level-history-list">
      <div className="divide-y divide-[var(--parchment)] max-h-64 overflow-y-auto">
        {data.history.map((h) => (
          <div key={h.id} className="py-2 flex items-center justify-between gap-3 text-sm" data-testid={`level-history-entry-${h.id}`}>
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-mono-spec text-xs text-[var(--muted)] shrink-0">{fmtDateTime(h.changed_at)}</span>
              <span className="text-xs text-[var(--ink-soft)] shrink-0">{h.subject?.code || h.subject?.name}</span>
              <span className="flex items-center gap-1 font-medium">
                {h.from_level || <span className="text-[var(--muted)] italic font-normal">Non inscrit</span>}
                <ArrowRight size={13} className="text-[var(--maroon)]" />
                {h.to_level || <span className="text-[var(--muted)] italic font-normal">Non inscrit</span>}
              </span>
            </div>
            <span className="text-[11px] text-[var(--muted)] truncate">{h.changed_by_name}</span>
          </div>
        ))}
      </div>
      {data.student && (
        <button
          className="mt-3 btn-secondary !py-1.5 !px-3 text-xs inline-flex items-center gap-1.5"
          onClick={() => printHistory(data)}
          data-testid="print-level-history-btn"
        >
          <Printer size={13} /> Imprimer pour la famille
        </button>
      )}
    </div>
  );
}

export default function LevelHistoryDialog({ student, onClose }) {
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent data-testid="level-history-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl flex items-center gap-2">
            <History size={20} className="text-[var(--maroon)]" /> Progression des niveaux
          </DialogTitle>
          <DialogDescription>
            {student.last_name.toUpperCase()} {student.first_name} — historique des changements de niveau de langue.
          </DialogDescription>
        </DialogHeader>
        <LevelHistoryContent studentId={student.id} />
      </DialogContent>
    </Dialog>
  );
}
